/**
 * Un-Synced platform: virtual platform for users without a connected fantasy account.
 * Leagues are local (frontend-generated league_id), persisted via user preferences.
 * userDataCache keeps integration_name null; endpoints that talk to POC map null → "un_synced"
 * via resolveUnsyncedPocIntegrationName(platformId) (common / super / chatbot / news / all_rankings).
 */

export const UNSYNCED_PLATFORM_ID = "Un_synced";
export const UNSYNCED_PLATFORM_NAME = "Un-Synced";

/** POC enum value for Un-Synced (common / super / chatbot / news / all_rankings). */
export const UNSYNCED_INTEGRATION_NAME = "un_synced";

/** @deprecated Prefer createUnsyncedLeagueId() — kept for reference only. */
export const UNSYNCED_DEFAULT_LEAGUE_ID = "unsynced-default";
export const UNSYNCED_DEFAULT_LEAGUE_NAME = "Un-Synced League";

/**
 * Agent button labels that require a real synced league.
 * Toggle by adding/removing a label here ("Rosters", "Trades", "Team Analysis", "Start/Sit", "Waivers", "Draft Advice", "Chatbot", "Rankings", "News", "Articles" etc.).
 * To enable one later for Un-Synced, remove it from this list.
 */
export const UNSYNCED_BLOCKED_AGENT_LABELS = ["Rosters", "Trades", "Team Analysis", "Start/Sit", "Waivers", "Draft Advice"] as const;

export type UnsyncedBlockedAgentLabel = (typeof UNSYNCED_BLOCKED_AGENT_LABELS)[number];

export function isUnsyncedPlatform(platformId: string | null | undefined): boolean {
  return platformId === UNSYNCED_PLATFORM_ID;
}

export function isUnsyncedBlockedAgent(label: string): boolean {
  return (UNSYNCED_BLOCKED_AGENT_LABELS as readonly string[]).includes(label);
}

/**
 * Map stored credentials integration_name for POC endpoints that require the un_synced enum.
 * Only when platformId is Un-Synced AND integration_name is null/empty.
 * Leave other platforms unchanged (incl. accidental null — do not coerce to un_synced).
 * News/rankings also use this (Un-Synced only via platformId guard).
 */
export function resolveUnsyncedPocIntegrationName(
  integrationName: string | null | undefined,
  platformId?: string | null,
): string | null {
  if (isUnsyncedPlatform(platformId) && (integrationName == null || integrationName === "")) {
    return UNSYNCED_INTEGRATION_NAME;
  }
  return integrationName ?? null;
}

/** Local unique league_id for user-created unsynced leagues. */
export function createUnsyncedLeagueId(): string {
  return `unsynced-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export function createUnsyncedLeague(name: string, leagueId?: string) {
  return {
    integration_name: null as null,
    league_id: leagueId ?? createUnsyncedLeagueId(),
    name,
    sport: "nfl",
  };
}

/** Seed one local league with a unique league_id (generated once per call; persist via preferences). */
export function getDefaultUnsyncedTeamsData() {
  return {
    leagues: [createUnsyncedLeague(UNSYNCED_DEFAULT_LEAGUE_NAME)],
  };
}

/**
 * One-time prefs migration: replace legacy fixed "unsynced-default" with a unique id.
 * No-op for leagues that already use createUnsyncedLeagueId(). Un-Synced only.
 */
export function migrateLegacyUnsyncedDefaultLeagueId(
  unsyncedTeams: { leagues?: Array<{ league_id: string; [key: string]: any }> } | null | undefined,
): {
  teamsData: { leagues?: Array<{ league_id: string; [key: string]: any }> } | null | undefined;
  newLeagueId: string | null;
} {
  if (!unsyncedTeams?.leagues?.length) {
    return { teamsData: unsyncedTeams, newLeagueId: null };
  }

  let newLeagueId: string | null = null;
  const leagues = unsyncedTeams.leagues.map((league) => {
    if (league.league_id !== UNSYNCED_DEFAULT_LEAGUE_ID) return league;
    newLeagueId = createUnsyncedLeagueId();
    return { ...league, league_id: newLeagueId };
  });

  if (!newLeagueId) {
    return { teamsData: unsyncedTeams, newLeagueId: null };
  }

  return { teamsData: { ...unsyncedTeams, leagues }, newLeagueId };
}

/** Synthetic credentials so API builders can read fields without crashing (all null). */
export function getUnsyncedUserData() {
  return {
    integration_name: null as null,
    token: null as null,
    user_id: null as null,
    error: null as null,
  };
}

/** Build selectedTeams entry for an unsynced league (call after the league exists in teamsDataCache). */
export function getDefaultUnsyncedSelectedTeam(leagueId: string) {
  return {
    platformId: UNSYNCED_PLATFORM_ID,
    teamName: UNSYNCED_DEFAULT_LEAGUE_NAME,
    leagueId,
  };
}
